/* eslint-disable class-methods-use-this */
import _ from 'lodash';
import {
  DiningOptionsBehavior,
  DiningOptionsOrderType,
  DiscountType,
  OrderSource,
  OrderStatus,
  ServiceChargeApplySource,
  ServiceChargeBehavior,
  ServiceChargeUsageType,
} from '../enums';
import {
  CompItemData,
  OrderData,
  OrderDestinationData,
  OrderedItem,
  OrderedItemDiscountVoucher,
  OrderedItemModifier,
  OrderedItemTax,
  OrderServiceCharge,
} from '../interfaces';
import {
  DiningOption,
  DiscountData,
  MenuItem,
  MenuItemData,
  ModifierGroup,
  Order,
  ServiceChargeData,
  TaxCodeData,
} from '../models';
import TaxCalculator from './tax-calculator';

export default class OrderHelper {
  private activeDiningOption: DiningOption;

  constructor(
    private menuItemById: Record<string, MenuItem>,
    private taxesById: Record<string, any>,
    private modifierGroupsById: Record<string, ModifierGroup>,
    private usesV2 = false
  ) {}

  public static nameFromStatus(status: OrderStatus): string {
    switch (status) {
      case OrderStatus.POINT_OF_SALE:
        return 'tab';
      default:
        return status;
    }
  }

  public static nameFromOrderType(orderType: DiningOptionsOrderType): string {
    switch (orderType) {
      case DiningOptionsOrderType.FUTURE:
        return 'Future';
      default:
        return 'ASAP';
    }
  }

  public updateMenuItemsById(menuItemsById: Record<string, any>) {
    this.menuItemById = menuItemsById;
  }

  public setActiveDiningOption(diningOption: DiningOption) {
    this.activeDiningOption = diningOption;
  }

  private canAddV2(item: OrderedItem): boolean {
    const menuItem = _.get(this.menuItemById, item.item) || { data: {} as MenuItemData };
    const { availableQuantity } = menuItem.data;
    if (availableQuantity < 0) {
      return true;
    }
    if (availableQuantity >= item.quantity) {
      return true;
    }
    return false;
  }

  public canAdd(item: OrderedItem): boolean {
    if (this.usesV2) {
      return this.canAddV2(item);
    }
    const menuItem = _.get(this.menuItemById, item.item) || { data: {} as MenuItemData };
    const { inventoryItem } = menuItem.data;
    if (!inventoryItem?.isTracked) {
      return true;
    }
    if (inventoryItem?.isTracked && inventoryItem.quantity >= item.quantity) {
      return true;
    }
    return false;
  }

  private getTaxesFromOrderedItem(orderedItem: OrderedItem): TaxCodeData[] {
    const menuItem = _.get(this.menuItemById, orderedItem.item) || { data: { taxCodes: [] } };
    return (menuItem.data.taxCodes || [])
      .map((taxId: string) => this.taxesById[taxId])
      .filter(Boolean);
  }

  public setItemTaxes(orderedItem: OrderedItem) {
    const itemDiscountTotal = orderedItem.discountTotal;
    const menuItem = _.get(this.menuItemById, orderedItem.item) || { data: { taxCodes: [] } };
    const { unitPrice, quantity } = orderedItem;
    const itemPrice = unitPrice * quantity - itemDiscountTotal;
    orderedItem.taxes = (menuItem.data.taxCodes || [])
      .map((tax: any) => {
        if (!tax?._id) {
          return null;
        }
        const rate = (tax?.taxRate || 0) * 0.01;
        const total = Math.ceil(rate * itemPrice);
        return {
          id: tax._id,
          total,
        };
      })
      .filter(Boolean);
    orderedItem.itemTaxTotal = orderedItem.taxes.reduce((prev: number, curr: OrderedItemTax) => {
      return prev + curr.total;
    }, 0);
  }

  public setTaxes(orderedItem: OrderedItem, taxExempt = false) {
    if (!this.usesV2) {
      this.setItemTaxes(orderedItem);
      orderedItem.taxTotal = orderedItem.itemTaxTotal;
      return;
    }
    const taxCodes = this.getTaxesFromOrderedItem(orderedItem);
    TaxCalculator.setTaxesForOrderedItem(orderedItem, taxCodes, taxExempt);
    TaxCalculator.setTaxesForOrderedItemModifiers(
      orderedItem,
      this.taxesById,
      this.modifierGroupsById,
      taxExempt
    );
    orderedItem.taxTotal = orderedItem.itemTaxTotal + orderedItem.modifierTaxTotal;
    orderedItem.taxIncludedTaxTotal = orderedItem.taxIncluded ? orderedItem.itemTaxTotal : 0;
    orderedItem.taxIncludedTaxTotal += orderedItem.modifiers.reduce(
      (taxIncludedTotal, modifier) => {
        if (modifier.taxIncluded) {
          return taxIncludedTotal + modifier.taxTotal;
        }
        return taxIncludedTotal;
      },
      0
    );
  }

  public canUseDiscount(item: OrderedItem['item'], discount: DiscountData): boolean {
    const menuItem = _.get(this.menuItemById, item);
    if (!_.size(menuItem?.data.discounts)) {
      return false;
    }
    return _.reduce(
      menuItem?.data.discounts,
      (prev: boolean, curr: DiscountData) => {
        if (discount._id === curr._id) {
          return true;
        }
        return prev;
      },
      false
    );
  }

  private getItemDiscountTotal(item: OrderedItem): number {
    const itemDiscountTotal = item.discounts?.reduce(
      (currTotal: number, itemDiscount: OrderedItemDiscountVoucher) => {
        return currTotal + itemDiscount.total || 0;
      },
      0
    );
    const modifierDiscountTotal = item.modifiers?.reduce(
      (modDiscountTotal: number, modifier: OrderedItemModifier) => {
        return (
          modDiscountTotal +
          modifier.discounts?.reduce(
            (currTotal: number, modifierDiscount: OrderedItemDiscountVoucher) => {
              return currTotal + modifierDiscount.total || 0;
            },
            0
          )
        );
      },
      0
    );
    return itemDiscountTotal + modifierDiscountTotal;
  }

  public calculateItemDiscount(item: OrderedItem, order: Order, discount: DiscountData): number {
    switch (discount.type) {
      case DiscountType.FIXED_AMOUNT: {
        const total = order.data.items.reduce((prev: number, curr: OrderedItem) => {
          return prev + this.getItemDiscountTotal(curr);
        }, 0);
        if (item.unitPrice * item.quantity + total <= discount.value) {
          return item.unitPrice * item.quantity;
        }
        return discount.value - total;
      }
      case DiscountType.PERCENT:
        return Math.ceil(item.unitPrice * item.quantity * (discount.value * 0.01));
      case DiscountType.COMPLIMENTARY:
        return item.unitPrice;
    }
    return 0;
  }

  public calculateModifierDiscount(
    modifier: OrderedItemModifier,
    order: Order,
    discount: DiscountData,
    itemQuantity: number
  ): number {
    switch (discount.type) {
      case DiscountType.FIXED_AMOUNT: {
        const total = order.data.items.reduce((prev: number, curr: OrderedItem) => {
          return prev + this.getItemDiscountTotal(curr);
        }, 0);
        if (modifier.unitPrice * modifier.quantity * itemQuantity + total <= discount.value) {
          return modifier.unitPrice * modifier.quantity * itemQuantity;
        }
        return discount.value - total;
      }
      case DiscountType.PERCENT:
        return Math.ceil(
          modifier.unitPrice * modifier.quantity * itemQuantity * (discount.value * 0.01)
        );
      case DiscountType.COMPLIMENTARY:
        return modifier.unitPrice;
    }
    return 0;
  }

  public clearDiscount(order: Order) {
    order.data.items.forEach((item: OrderedItem) => {
      item.discounts = [];
      item.itemDiscountTotal = 0;
      item.modifierDiscountTotal = 0;
      item.discountTotal = 0;
      item.modifiers.forEach(modifier => {
        modifier.discounts = [];
        modifier.discountTotal = 0;
      });
      this.setTaxes(item, order.data.taxExempt);
    });
  }

  public setDiscount(
    order: Order,
    discount: DiscountData,
    employeeId: string,
    adminId?: any
  ): void {
    order.data.items.forEach((item: OrderedItem) => {
      item.discounts = [];
      item.itemDiscountTotal = 0;
      item.modifierDiscountTotal = 0;
      item.discountTotal = 0;
      item.modifiers.forEach(modifier => {
        modifier.discounts = [];
        modifier.discountTotal = 0;
      });
      if (this.canUseDiscount(item.item, discount)) {
        const itemDiscountValue = this.calculateItemDiscount(item, order, discount);
        if (itemDiscountValue) {
          item.itemDiscountTotal = itemDiscountValue;
          item.discounts.push({
            id: discount._id,
            total: item.itemDiscountTotal,
            type: discount.type,
            appliedBy: employeeId,
            appliedAt: new Date(),
            approvedBy: adminId,
            approvedAt: adminId ? new Date() : null,
          });
        }
        item.modifiers.forEach(modifier => {
          const modififierDiscountValue = this.calculateModifierDiscount(
            modifier,
            order,
            discount,
            item.quantity
          );
          if (modififierDiscountValue) {
            modifier.discountTotal = modififierDiscountValue;
            modifier.discounts.push({
              id: discount._id,
              total: modifier.discountTotal,
              type: discount.type,
              appliedBy: employeeId,
              appliedAt: new Date(),
              approvedBy: adminId,
              approvedAt: adminId ? new Date() : null,
            });
          }
        });
        item.modifierDiscountTotal = item.modifiers.reduce(
          (prev: number, curr: OrderedItemModifier) => {
            return prev + curr.discountTotal;
          },
          0
        );
        item.discountTotal = item.itemDiscountTotal + item.modifierDiscountTotal;
        this.setTaxes(item, order.data.taxExempt);
      }
    });
  }

  public setCompDiscount(
    order: Order,
    discount: DiscountData,
    compItems: CompItemData[],
    employeeId: string,
    adminId?: any
  ): any {
    order.data.items.forEach((item: OrderedItem, i: number) => {
      if (compItems[i].compQuantity && this.canUseDiscount(item.item, discount)) {
        item.discounts = [];
        const itemDiscountValue = this.calculateItemDiscount(item, order, discount);
        if (itemDiscountValue) {
          item.itemDiscountTotal = itemDiscountValue * compItems[i].compQuantity;
          item.discounts.push({
            id: discount._id,
            total: item.itemDiscountTotal,
            type: discount.type,
            appliedBy: employeeId,
            appliedAt: new Date(),
            approvedBy: adminId,
            approvedAt: adminId ? new Date() : null,
          });
        }
        item.modifiers.forEach(modifier => {
          const modififierDiscountValue = this.calculateModifierDiscount(
            modifier,
            order,
            discount,
            item.quantity
          );
          if (modififierDiscountValue) {
            modifier.discountTotal = modififierDiscountValue * compItems[i].compQuantity;
            modifier.discounts.push({
              id: discount._id,
              total: modifier.discountTotal,
              type: discount.type,
              appliedBy: employeeId,
              appliedAt: new Date(),
              approvedBy: adminId,
              approvedAt: adminId ? new Date() : null,
            });
          }
        });
        item.modifierDiscountTotal = item.modifiers.reduce(
          (prev: number, curr: OrderedItemModifier) => {
            return prev + curr.discountTotal;
          },
          0
        );
        item.discountTotal = item.itemDiscountTotal + item.modifierDiscountTotal;
        this.setTaxes(item, order.data.taxExempt);
      }
    });
  }

  /**
   * @todo TransactionData
   */
  public updateGratuityWithPercentage(transaction: any, gratuity: any) {
    const gratValue = gratuity.value * 0.01;
    transaction.gratuity = Math.ceil((transaction.total - transaction.gratuity) * gratValue);
    transaction.amount =
      transaction.subTotal - transaction.discount + transaction.taxTotal + transaction.serviceFee;
    transaction.total = transaction.amount + transaction.gratuity;
    return transaction;
  }

  /**
   * @todo TransactionData
   */
  public updateGratuityWithAmount(transaction: any, amount: number) {
    transaction.gratuity = amount;
    transaction.amount =
      transaction.subTotal - transaction.discount + transaction.taxTotal + transaction.serviceFee;
    transaction.total = transaction.amount + transaction.gratuity;
    return transaction;
  }

  public static isItemExcludedFromServiceCharge(
    menuItem: MenuItemData | undefined,
    serviceCharge: ServiceChargeData,
    orderedItemId: string
  ): boolean {
    // Match API: legacy / merged charge exclusions win before item-level usage type.
    // Menu payloads always resolve usage type to at least `all`, so checking exclusions
    // only in a fallback would silently ignore per-charge excludeMenuItems.
    const excludeIds = (serviceCharge.excludeMenuItems || []).map(String);
    if (excludeIds.includes(String(orderedItemId))) {
      return true;
    }

    if (menuItem?.serviceChargeUsageType) {
      const chargeId = String(serviceCharge._id);
      const listed = (menuItem.serviceCharges || []).map(String);
      switch (menuItem.serviceChargeUsageType) {
        case ServiceChargeUsageType.NONE:
          return true;
        case ServiceChargeUsageType.INCLUDE_SERVICE_CHARGES:
          return !listed.includes(chargeId);
        case ServiceChargeUsageType.EXCLUDE_SERVICE_CHARGES:
          return listed.includes(chargeId);
        case ServiceChargeUsageType.ALL:
        default:
          return false;
      }
    }

    return false;
  }

  public static getServiceChargeAmount(
    order: Order,
    serviceCharge: ServiceChargeData,
    menuItemById?: Record<string, MenuItem>
  ): number {
    const subtractTotal = order.data.items
      .filter((orderedItem: OrderedItem) => {
        const menuItem = menuItemById?.[orderedItem.item]?.data;
        return OrderHelper.isItemExcludedFromServiceCharge(
          menuItem,
          serviceCharge,
          orderedItem.item
        );
      })
      .reduce((acc: number, item: OrderedItem) => acc + item.price, 0);
    switch (serviceCharge.behavior) {
      case ServiceChargeBehavior.FIXED_AMOUNT:
        return serviceCharge.value;
      case ServiceChargeBehavior.PERCENTAGE_ON_SUB_TOTAL:
        return Math.ceil(Math.max(0, order.data.subTotal - subtractTotal) * serviceCharge.value);
      case ServiceChargeBehavior.PERCENTAGE_ON_TOTAL: {
        // Exclude existing service fees so reprice does not compound on prior charges.
        const totalBeforeServiceCharges =
          (order.data.total || 0) - (order.data.serviceFee || 0);
        return Math.ceil(
          Math.max(0, totalBeforeServiceCharges - subtractTotal) * serviceCharge.value
        );
      }
      case ServiceChargeBehavior.PERCENTAGE_PRE_TAX:
        return Math.ceil(
          Math.max(0, order.data.subTotal - subtractTotal - order.data.discount) *
            serviceCharge.value
        );
    }
  }

  /**
   * Replace order service charges with the active dining option's auto charges.
   * Used on create / dining-option change — not on add/remove item.
   * Skips charges in suppressedServiceChargeIds unless suppressions were just cleared.
   */
  public applyServiceCharges(order: Order) {
    if (!this.activeDiningOption) {
      return;
    }
    if (!order.data.items.length) {
      order.data.serviceCharges = [];
      order.data.serviceFee = 0;
      order.data.partakeServiceFee = 0;
      return;
    }
    const suppressed = new Set(
      (order.data.suppressedServiceChargeIds || []).map(id => String(id))
    );
    const serviceCharges = this.activeDiningOption.data.serviceCharges;
    const orderServiceCharges: OrderServiceCharge[] = [];
    let serviceChargeTotal = 0;
    let partakeServiceChargeTotal = 0;
    (serviceCharges as ServiceChargeData[])?.forEach(serviceCharge => {
      if (serviceCharge.isPartake || serviceCharge.isTenderCharge) {
        return;
      }
      if (suppressed.has(String(serviceCharge._id))) {
        return;
      }
      const orderServiceCharge: OrderServiceCharge = {
        name: serviceCharge.name,
        amount: OrderHelper.getServiceChargeAmount(order, serviceCharge, this.menuItemById),
        serviceChargeId: serviceCharge._id,
        isPartake: !!serviceCharge.isPartake,
        isTenderCharge: false,
        source: ServiceChargeApplySource.AUTO,
      };
      orderServiceCharges.push(orderServiceCharge);
      serviceChargeTotal += orderServiceCharge.amount;
      if (orderServiceCharge.isPartake) {
        partakeServiceChargeTotal += orderServiceCharge.amount;
      }
    });
    order.data.serviceCharges = orderServiceCharges;
    order.data.serviceFee = serviceChargeTotal;
    order.data.partakeServiceFee = partakeServiceChargeTotal;
  }

  /**
   * Attach unsuppressed dining-option auto charges and reprice every charge
   * already on the order. Manual charges keep appliedBy. Suppressed charges stay off.
   */
  public syncDiningServiceCharges(order: Order, catalog: ServiceChargeData[] = []): void {
    if (!order.data.items.length) {
      order.data.serviceCharges = [];
      order.data.serviceFee = 0;
      order.data.partakeServiceFee = 0;
      return;
    }
    const suppressed = new Set(
      (order.data.suppressedServiceChargeIds || []).map(id => String(id))
    );
    const existing = (order.data.serviceCharges || []).filter(
      charge => !suppressed.has(String(charge.serviceChargeId))
    );
    const byId = new Map(existing.map(charge => [String(charge.serviceChargeId), { ...charge }]));
    const catalogById = new Map((catalog || []).map(charge => [String(charge._id), charge]));
    const diningCharges = (this.activeDiningOption?.data?.serviceCharges || []) as Array<
      string | ServiceChargeData
    >;
    const resolveDiningCharge = (
      entry: string | ServiceChargeData
    ): ServiceChargeData | undefined => {
      if (!entry) {
        return undefined;
      }
      if (typeof entry === 'string') {
        return catalogById.get(entry);
      }
      return entry;
    };
    const diningById = new Map<string, ServiceChargeData>();
    diningCharges.forEach(entry => {
      const resolved = resolveDiningCharge(entry);
      if (!resolved?._id) {
        return;
      }
      const id = String(resolved._id);
      diningById.set(id, resolved);
      if (resolved.isPartake || resolved.isTenderCharge || suppressed.has(id) || byId.has(id)) {
        return;
      }
      byId.set(id, {
        name: resolved.name,
        amount: 0,
        serviceChargeId: resolved._id,
        isPartake: false,
        isTenderCharge: false,
        source: ServiceChargeApplySource.AUTO,
      });
    });
    let serviceChargeTotal = 0;
    let partakeServiceChargeTotal = 0;
    order.data.serviceCharges = Array.from(byId.values()).map(charge => {
      const def =
        catalogById.get(String(charge.serviceChargeId)) ||
        diningById.get(String(charge.serviceChargeId));
      const amount = def
        ? OrderHelper.getServiceChargeAmount(order, def, this.menuItemById)
        : charge.amount;
      const next = { ...charge, amount };
      serviceChargeTotal += amount;
      if (next.isPartake) {
        partakeServiceChargeTotal += amount;
      }
      return next;
    });
    order.data.serviceFee = serviceChargeTotal;
    order.data.partakeServiceFee = partakeServiceChargeTotal;
  }

  /**
   * Recalculate amounts for charges already on the order without adding dining autos
   * or dropping manuals. Keeps intentional removals intact across item edits.
   */
  public repriceExistingServiceCharges(
    order: Order,
    catalog: ServiceChargeData[] = []
  ): void {
    if (!order.data.items.length) {
      order.data.serviceCharges = [];
      order.data.serviceFee = 0;
      order.data.partakeServiceFee = 0;
      return;
    }
    const existing = order.data.serviceCharges || [];
    if (!existing.length) {
      order.data.serviceFee = 0;
      order.data.partakeServiceFee = 0;
      return;
    }
    const catalogById = new Map((catalog || []).map(c => [String(c._id), c]));
    let serviceChargeTotal = 0;
    let partakeServiceChargeTotal = 0;
    order.data.serviceCharges = existing.map(charge => {
      const def = catalogById.get(String(charge.serviceChargeId));
      const amount = def
        ? OrderHelper.getServiceChargeAmount(order, def, this.menuItemById)
        : charge.amount;
      const next = { ...charge, amount };
      serviceChargeTotal += amount;
      if (next.isPartake) {
        partakeServiceChargeTotal += amount;
      }
      return next;
    });
    order.data.serviceFee = serviceChargeTotal;
    order.data.partakeServiceFee = partakeServiceChargeTotal;
  }

  private addSuppressedServiceChargeIds(order: Order, serviceChargeIds: string[]): void {
    const next = new Set((order.data.suppressedServiceChargeIds || []).map(id => String(id)));
    serviceChargeIds.forEach(id => {
      if (id) {
        next.add(String(id));
      }
    });
    order.data.suppressedServiceChargeIds = Array.from(next);
  }

  private clearSuppressedServiceChargeId(order: Order, serviceChargeId: string): void {
    if (!serviceChargeId || !order.data.suppressedServiceChargeIds?.length) {
      return;
    }
    order.data.suppressedServiceChargeIds = order.data.suppressedServiceChargeIds.filter(
      id => String(id) !== String(serviceChargeId)
    );
  }

  public applyManualServiceCharge(
    order: Order,
    serviceCharge: ServiceChargeData,
    employeeId?: string,
    adminId?: string
  ): boolean {
    if (!serviceCharge || serviceCharge.isPartake || serviceCharge.isTenderCharge) {
      return false;
    }
    if (!order.data.items.length) {
      return false;
    }
    const alreadyApplied = (order.data.serviceCharges || []).some(
      charge => String(charge.serviceChargeId) === String(serviceCharge._id)
    );
    if (alreadyApplied) {
      return false;
    }
    const now = new Date();
    const orderServiceCharge: OrderServiceCharge = {
      name: serviceCharge.name,
      amount: OrderHelper.getServiceChargeAmount(order, serviceCharge, this.menuItemById),
      serviceChargeId: serviceCharge._id,
      isPartake: !!serviceCharge.isPartake,
      source: ServiceChargeApplySource.MANUAL,
      appliedBy: employeeId,
      appliedAt: now,
      approvedBy: adminId,
      approvedAt: adminId ? now : undefined,
    };
    if (!order.data.serviceCharges) {
      order.data.serviceCharges = [];
    }
    order.data.serviceCharges.push(orderServiceCharge);
    this.clearSuppressedServiceChargeId(order, String(serviceCharge._id));
    return true;
  }

  public removeServiceCharge(order: Order, serviceChargeId: string): boolean {
    if (!serviceChargeId || !order.data.serviceCharges?.length) {
      return false;
    }
    const next = order.data.serviceCharges.filter(
      charge => String(charge.serviceChargeId) !== String(serviceChargeId)
    );
    if (next.length === order.data.serviceCharges.length) {
      return false;
    }
    order.data.serviceCharges = next;
    this.addSuppressedServiceChargeIds(order, [String(serviceChargeId)]);
    return true;
  }

  /** Clears all service charges on the order, including dining-option auto charges. */
  public clearServiceCharges(order: Order): void {
    const removedIds = (order.data.serviceCharges || [])
      .map(charge => String(charge.serviceChargeId))
      .filter(Boolean);
    order.data.serviceCharges = [];
    order.data.serviceFee = 0;
    order.data.partakeServiceFee = 0;
    this.addSuppressedServiceChargeIds(order, removedIds);
  }

  public updateOrderDestination(
    order: Order,
    diningOption: DiningOption,
    orderDestination: OrderDestinationData
  ) {
    this.activeDiningOption = diningOption;
    order.data.orderDestination = orderDestination;
    order.data.suppressedServiceChargeIds = [];
    this.applyServiceCharges(order);
  }

  public static updateItemPrice(
    unitPrice: number,
    quantity: number,
    modifiers: OrderedItemModifier[]
  ) {
    let price = quantity * unitPrice;
    if (modifiers.length) {
      modifiers.forEach(modifier => {
        const modifierQuantity = modifier.quantity || 1;
        const hasPositiveExplicitPrice =
          typeof modifier.price === 'number' &&
          Number.isFinite(modifier.price) &&
          modifier.price > 0;
        const perItemModifierPrice = hasPositiveExplicitPrice
          ? modifier.price / modifierQuantity
          : modifier.unitPrice || 0;
        const modPrice = perItemModifierPrice * modifierQuantity;
        price += quantity * modPrice;
      });
    }
    return price;
  }

  public defaultCart(
    restaurant: any,
    shiftId: string,
    orderDestination: OrderDestinationData,
    diningOption: DiningOption,
    guest?: any,
    member?: string
  ): Order {
    this.activeDiningOption = diningOption;
    const order = new Order({
      venue: restaurant.venue?._id || restaurant.venue,
      restaurant: restaurant._id,
      shift: shiftId,
      isConsumerOrder: false,
      status: OrderStatus.POINT_OF_SALE,
      source: OrderSource.POS_APP, // Make based on BUILD_SETTING
      orderDestination,
      guest,
      member,
      user: null,
      items: [],
      removed: [],
      void: [],
      subTotal: 0,
      discount: 0,
      taxTotal: 0,
      partakeTax: 0,
      gratuity: 0,
      serviceFee: 0,
      partakeServiceFee: 0,
      serviceCharges: [],
      suppressedServiceChargeIds: [],
      roundUpTotal: 0,
      total: 0,
      amountPaid: 0,
      due: 0,
      alcoholCount: 0,
      shouldRoundUp: false,
      isFirstOrder: false,
      created: new Date(),
    } as OrderData);
    this.applyServiceCharges(order);
    return order;
  }

  static filterOpenOrders(orders: Order[], locationId: string): Order[] {
    return orders.filter(order => {
      const orderLocationId = (order.data.restaurant as any)?._id || order.data.restaurant;
      if (!order.data.isConsumerOrder) {
        return true;
      }
      if (order.data.isConsumerOrder && orderLocationId === locationId) {
        return true;
      }
      return false;
    });
  }

  static filterOpenOrdersByBehavior(orders: Order[], behaviors: DiningOptionsBehavior[]): Order[] {
    return orders.filter(order => {
      return behaviors.includes(order.data.orderDestination.behaviour);
    });
  }
}
